import React, { useState, useEffect } from 'react';
import { Home, Target, FileText, BarChart3, Users, Award } from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';
import { useAuth } from '../../App';
import LanguageSelector from '../LanguageSelector';
import ObjectivesList from './ObjectivesList';
import EvaluationsList from './EvaluationsList';
import Feedback360 from './Feedback360';
import SkillsManagement from './SkillsManagement';
import PerformanceStats from './PerformanceStats';

function PerformanceDashboard({ user: userProp, onNavigate }) {
  const { t } = useTranslation();
  const { user: userFromAuth } = useAuth();
  const user = userProp || userFromAuth;
  const [activeTab, setActiveTab] = useState('objectives');
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const tabs = [
    { id: 'objectives', label: t('performance.objectives'), icon: Target },
    { id: 'evaluations', label: t('performance.evaluations'), icon: FileText },
    { id: 'feedback360', label: t('performance.feedback360'), icon: Users },
    { id: 'skills', label: t('performance.skills'), icon: Award },
    { id: 'stats', label: t('performance.stats'), icon: BarChart3 }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header transparent comme IdeasList et NotesFraisManagement */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '16px'
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('common.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{
            fontSize: 'clamp(28px, 5vw, 40px)',
            fontWeight: 700,
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            minWidth: isMobile ? 0 : undefined,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <BarChart3 size={32} />
            {t('performance.title')}
          </div>
          {!isMobile && (
            <div style={{ flexShrink: 0 }}>
              <LanguageSelector style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 10px', borderRadius: 10 }} />
            </div>
          )}
        </div>
      </div>

      {/* Contenu principal - responsive padding on mobile */}
      <div className="performance-content" style={{
        paddingTop: '140px',
        paddingBottom: '40px',
        paddingLeft: isMobile ? '16px' : '20px',
        paddingRight: isMobile ? '16px' : '20px',
        maxWidth: '1600px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Language translator in body on mobile only - clear separation from header and tabs */}
        {isMobile && (
          <div style={{
            marginBottom: '20px',
            padding: '14px 16px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '12px'
          }}>
            <LanguageSelector style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', padding: '8px 12px', borderRadius: 10 }} />
          </div>
        )}
        {/* Onglets */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          flexWrap: 'wrap',
          overflowX: 'auto',
          paddingBottom: '8px'
        }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '12px 24px',
                  background: isActive
                    ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                    : 'rgba(255, 255, 255, 0.1)',
                  border: isActive
                    ? 'none'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: isActive ? 600 : 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s',
                  backdropFilter: 'blur(10px)',
                  boxShadow: isActive ? '0 4px 16px rgba(59, 130, 246, 0.3)' : 'none',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  }
                }}
              >
                <Icon size={20} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Contenu des onglets */}
        <div>
          {activeTab === 'objectives' && (
            <ObjectivesList user={user} onNavigate={onNavigate} hideHeader={true} t={t} />
          )}
          {activeTab === 'evaluations' && (
            <EvaluationsList user={user} onNavigate={onNavigate} hideHeader={true} t={t} />
          )}
          {activeTab === 'feedback360' && (
            <Feedback360 user={user} onNavigate={onNavigate} hideHeader={true} t={t} />
          )}
          {activeTab === 'skills' && (
            <SkillsManagement user={user} onNavigate={onNavigate} hideHeader={true} t={t} />
          )}
          {activeTab === 'stats' && (
            <PerformanceStats user={user} onNavigate={onNavigate} hideHeader={true} t={t} />
          )}
        </div>
      </div>

      {/* CSS pour animations et responsive */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .performance-content {
            padding-top: clamp(100px, 12vw, 140px) !important;
            padding-bottom: 40px !important;
            padding-left: 40px !important;
            padding-right: 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .performance-content {
            padding-top: clamp(100px, 12vw, 140px) !important;
            padding-bottom: 60px !important;
            padding-left: 60px !important;
            padding-right: 60px !important;
          }
        }
        .performance-content {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}

export default PerformanceDashboard;